// src/lib/websocketManager.ts
// =========================================================
//      Unified WebSocket Manager
// =========================================================

import { logger } from "../utils/logging/logger";
import { getDeviceIdAsync } from "../utils/keyManager";
import { buildWsUrl } from "../utils/realtime/ws";
import { canRunPrivateIntegrationsNow } from "../utils/security/privateIntegrationGate";

export type WsModule =
  | "presence"
  | "conversation"
  | "comments"
  | "notifications"
  | "reactions"
  | "sanctuary";

const WS_HARD_DISABLE = false;

const WS_CLOSE_RESTRICTED_OWNER = 4409;

type WsHandler = (data: any) => void;

class SingleWebSocketManager {
  private socket: WebSocket | null = null;
  private isConnecting = false;
  private token: string | null = null;

  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 15;

  private registeredModules: Set<WsModule> = new Set();
  private eventHandlers: Record<string, WsHandler[]> = {};

  private intentional = false;
  private restrictedOwnerBlocked = false;
  private hardStopped = false;

  private reconnectTimers: Set<ReturnType<typeof setTimeout>> = new Set();

  public connect(token: string, _module: WsModule) {
    this.token = token;

    if (WS_HARD_DISABLE) return;
    if (this.hardStopped) return;
    if (!this.isUserLoggedIn()) return;
    if (!canRunPrivateIntegrationsNow()) return;
    if (this.restrictedOwnerBlocked) return;

    if (this.socket?.readyState === WebSocket.OPEN) return;
    if (this.isConnecting) return;

    void this.startConnection();
  }

  private async startConnection() {
    if (this.hardStopped) return;
    if (this.restrictedOwnerBlocked) return;
    if (!this.isUserLoggedIn()) return;
    if (!canRunPrivateIntegrationsNow()) return;

    this.isConnecting = true;
    this.intentional = false;

    try {
      await new Promise((resolve) => setTimeout(resolve, 400));

      if (this.hardStopped) {
        this.isConnecting = false;
        return;
      }

      if (!this.token) {
        logger.warn("[WS] Missing token; abort connect");
        this.isConnecting = false;
        return;
      }

      if (this.restrictedOwnerBlocked) {
        this.isConnecting = false;
        return;
      }

      if (!this.isUserLoggedIn()) {
        this.isConnecting = false;
        return;
      }

      if (!canRunPrivateIntegrationsNow()) {
        this.isConnecting = false;
        return;
      }

      const deviceId = (await getDeviceIdAsync()) || "";
      const wsUrl = buildWsUrl("", {
        token: this.token,
        device_id: deviceId,
      });

      if (
        this.hardStopped ||
        this.restrictedOwnerBlocked ||
        !this.isUserLoggedIn() ||
        !canRunPrivateIntegrationsNow()
      ) {
        this.isConnecting = false;
        return;
      }

      const socket = new WebSocket(wsUrl);
      this.socket = socket;

      socket.onopen = () => {
        this.isConnecting = false;
        this.intentional = false;
        this.reconnectAttempts = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleIncomingUnified(data);
        } catch (e) {
          logger.warn("[WS] Invalid JSON message", e);
        }
      };

      socket.onerror = (err) => {
        if (this.hardStopped) return;
        logger.warn("[WS] ERROR", err);
      };

      socket.onclose = (ev) => {
        if (this.hardStopped) return;

        logger.warn(`[WS] CLOSED code=${ev.code} reason=${ev.reason}`);

        this.socket = null;
        this.isConnecting = false;

        if (this.intentional) return;

        if (ev.code === WS_CLOSE_RESTRICTED_OWNER) {
          logger.warn("[WS] Restricted owner gate active; reconnect disabled");
          this.restrictedOwnerBlocked = true;
          this.registeredModules.clear();
          this.clearReconnectTimers();
          return;
        }

        if (ev.code === 4001 || ev.code === 401) {
          this.scheduleReconnect();
          return;
        }

        if (
          ev.code === 4403 ||
          (ev.reason || "").toUpperCase() === "POP_REQUIRED"
        ) {
          logger.warn("[WS] POP_REQUIRED → Awaiting keys:verified");

          window.addEventListener(
            "keys:verified",
            () => {
              if (this.hardStopped) return;
              this.reconnectAttempts = 0;
              void this.startConnection();
            },
            { once: true },
          );

          return;
        }

        this.scheduleReconnect();
      };
    } catch (err) {
      if (this.hardStopped) return;

      logger.error("[WS] Connection error", err);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  public hasEventsRegistered(module: WsModule) {
    return this.registeredModules.has(module);
  }

  public markEventsAsRegistered(module: WsModule) {
    this.registeredModules.add(module);
  }

  public clearRegisteredEvents(module: WsModule) {
    this.registeredModules.delete(module);
  }

  public sendMessage(module: WsModule, message: any) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }

    const input = (message ?? {}) as Record<string, any>;
    const { type, data, payload, app: _ignoredApp, ...rest } = input;

    if (!type || typeof type !== "string") {
      logger.warn("[WS] Missing message type; skipping send", input);
      return;
    }

    const canonicalData =
      data && typeof data === "object"
        ? data
        : payload && typeof payload === "object"
          ? payload
          : rest;

    const envelope = {
      app: module,
      type,
      data: canonicalData ?? {},
    };

    this.socket.send(JSON.stringify(envelope));
  }

  private handleIncomingUnified(raw: any) {
    if (raw?.type === "ping" && !raw?.app) {
      this.sendRaw({ type: "pong" });
      return;
    }

    if (!raw?.app) {
      this.emit(raw?.type, raw);
      return;
    }

    if (raw?.type === "event") {
      const eventPayload =
        raw?.data !== undefined
          ? raw.data
          : raw?.payload !== undefined
            ? raw.payload
            : {};

      this.emit(`${raw.app}:${raw.event}`, eventPayload);
      this.emit(raw.event, eventPayload);
      this.emit(raw.app, eventPayload);

      return;
    }

    const compatPayload =
      raw?.data !== undefined
        ? raw.data
        : raw?.payload !== undefined
          ? raw.payload
          : raw;

    this.emit(raw.app, compatPayload);
    this.emit(raw.type, compatPayload);
    this.emit(`${raw.app}:${raw.type}`, compatPayload);
  }

  private sendRaw(data: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(data));
    }
  }

  public on(key: string, callback: WsHandler) {
    if (!this.eventHandlers[key]) {
      this.eventHandlers[key] = [];
    }

    this.eventHandlers[key].push(callback);
  }

  public off(key: string, callback: WsHandler) {
    if (!this.eventHandlers[key]) return;

    this.eventHandlers[key] = this.eventHandlers[key].filter(
      (cb) => cb !== callback,
    );

    if (this.eventHandlers[key].length === 0) {
      delete this.eventHandlers[key];
    }
  }

  private emit(key: string | undefined, payload: any) {
    if (!key) return;

    const handlers = this.eventHandlers[key];
    if (!handlers?.length) return;

    handlers.forEach((cb) => {
      try {
        cb(payload);
      } catch (err) {
        logger.error(`[WS] Handler error for key ${key}`, err);
      }
    });
  }

  public isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  public disconnectAll() {
    this.intentional = true;
    this.clearReconnectTimers();

    try {
      this.socket?.close();
    } catch {
      // Ignore close failure.
    }

    this.socket = null;
    this.isConnecting = false;
    this.eventHandlers = {};
    this.registeredModules.clear();
  }

  public hardStopForLogout() {
    this.hardStopped = true;
    this.token = null;
    this.intentional = true;
    this.restrictedOwnerBlocked = false;
    this.reconnectAttempts = 0;

    this.clearReconnectTimers();

    try {
      this.socket?.close();
    } catch {
      // Ignore close failure.
    }

    this.socket = null;
    this.isConnecting = false;
    this.eventHandlers = {};
    this.registeredModules.clear();
  }

  public resetAfterLogin() {
    this.hardStopped = false;
    this.restrictedOwnerBlocked = false;
    this.reconnectAttempts = 0;
    this.intentional = false;
    this.clearReconnectTimers();
  }

  public resetRestrictedOwnerBlock() {
    this.restrictedOwnerBlocked = false;
  }

  private scheduleReconnect() {
    if (this.hardStopped) return;
    if (!this.isUserLoggedIn()) return;
    if (!canRunPrivateIntegrationsNow()) return;
    if (this.restrictedOwnerBlocked) return;

    this.reconnectAttempts++;

    if (this.reconnectAttempts > this.maxReconnectAttempts) {
      logger.warn("[WS] Max reconnect attempts reached");
      return;
    }

    const delay = Math.min(30000, 2000 * Math.pow(1.5, this.reconnectAttempts));
    const jitter = Math.random() * 3000;
    const total = delay + jitter;

    logger.warn(`[WS] Reconnecting in ${Math.round(total)}ms...`);

    const timer = setTimeout(() => {
      this.reconnectTimers.delete(timer);

      if (this.hardStopped) return;
      if (this.restrictedOwnerBlocked) return;
      if (!this.isUserLoggedIn()) return;
      if (!canRunPrivateIntegrationsNow()) return;

      void this.startConnection();
    }, total);

    this.reconnectTimers.add(timer);
  }

  private clearReconnectTimers() {
    this.reconnectTimers.forEach((timer) => {
      clearTimeout(timer);
    });

    this.reconnectTimers.clear();
  }

  private isUserLoggedIn() {
    return !!localStorage.getItem("access_token");
  }
}

const websocketManager = new SingleWebSocketManager();
export default websocketManager;

// // src/lib/websocketManager.ts
// // =========================================================
// //      Unified WebSocket Manager
// // =========================================================

// import { logger } from "../utils/logging/logger";
// import { getDeviceIdAsync } from "../utils/keyManager";
// import { buildWsUrl } from "../utils/realtime/ws";

// export type WsModule =
//   | "presence"
//   | "conversation"
//   | "comments"
//   | "notifications"
//   | "reactions"
//   | "sanctuary";

// const WS_HARD_DISABLE = false;

// type WsHandler = (data: any) => void;

// class SingleWebSocketManager {
//   // --------------------------------------------------
//   // Internal state
//   // --------------------------------------------------
//   private socket: WebSocket | null = null;
//   private isConnecting = false;
//   private token: string | null = null;

//   private reconnectAttempts = 0;
//   private readonly maxReconnectAttempts = 15;

//   private registeredModules: Set<WsModule> = new Set();

//   /** Event handlers registry */
//   private eventHandlers: Record<string, WsHandler[]> = {};

//   /** True when disconnect was requested intentionally */
//   private intentional = false;

//   // =========================================================
//   // PUBLIC → Connect
//   // =========================================================
//   public connect(token: string, _module: WsModule) {
//     this.token = token;

//     if (WS_HARD_DISABLE) return;
//     if (!this.isUserLoggedIn()) return;

//     if (this.socket?.readyState === WebSocket.OPEN) return;
//     if (this.isConnecting) return;

//     void this.startConnection();
//   }

//   // =========================================================
//   // INTERNAL → Start connection
//   // =========================================================
//   private async startConnection() {
//     this.isConnecting = true;
//     this.intentional = false;

//     try {
//       await new Promise((resolve) => setTimeout(resolve, 400)); // Small delay

//       if (!this.token) {
//         logger.warn("[WS] Missing token; abort connect");
//         this.isConnecting = false;
//         return;
//       }

//       const deviceId = (await getDeviceIdAsync()) || "";
//       const wsUrl = buildWsUrl("", {
//         token: this.token,
//         device_id: deviceId,
//       });

//       const socket = new WebSocket(wsUrl);
//       this.socket = socket;

//       // ------------------------------
//       socket.onopen = () => {
//         this.isConnecting = false;
//         this.intentional = false;
//         this.reconnectAttempts = 0;
//       };

//       // ------------------------------
//       socket.onmessage = (event) => {
//         try {
//           const data = JSON.parse(event.data);
//           this.handleIncomingUnified(data);
//         } catch (e) {
//           logger.warn("[WS] Invalid JSON message", e);
//         }
//       };

//       // ------------------------------
//       socket.onerror = (err) => {
//         logger.warn("[WS] ERROR", err);
//       };

//       // ------------------------------
//       socket.onclose = (ev) => {
//         logger.warn(`[WS] CLOSED code=${ev.code} reason=${ev.reason}`);

//         this.socket = null;
//         this.isConnecting = false;

//         if (this.intentional) return;

//         // Auth / token issues
//         if (ev.code === 4001 || ev.code === 401) {
//           this.scheduleReconnect();
//           return;
//         }

//         // PoP expired
//         if (ev.code === 4403 || (ev.reason || "").toUpperCase() === "POP_REQUIRED") {
//           logger.warn("[WS] POP_REQUIRED → Awaiting keys:verified");

//           window.addEventListener(
//             "keys:verified",
//             () => {
//               this.reconnectAttempts = 0;
//               void this.startConnection();
//             },
//             { once: true }
//           );

//           return;
//         }

//         // Normal reconnect
//         this.scheduleReconnect();
//       };
//     } catch (err) {
//       logger.error("[WS] Connection error", err);
//       this.isConnecting = false;
//       this.scheduleReconnect();
//     }
//   }

//   // =========================================================
//   // LEGACY REGISTRATION API
//   // =========================================================
//   public hasEventsRegistered(module: WsModule) {
//     return this.registeredModules.has(module);
//   }

//   public markEventsAsRegistered(module: WsModule) {
//     this.registeredModules.add(module);
//   }

//   public clearRegisteredEvents(module: WsModule) {
//     this.registeredModules.delete(module);
//   }

//   // =========================================================
//   // SEND → Canonical envelope
//   // =========================================================
//   public sendMessage(module: WsModule, message: any) {
//     if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
//       return;
//     }

//     const input = (message ?? {}) as Record<string, any>;
//     const { type, data, payload, app: _ignoredApp, ...rest } = input;

//     if (!type || typeof type !== "string") {
//       logger.warn("[WS] Missing message type; skipping send", input);
//       return;
//     }

//     // Canonical outbound payload is always `data`
//     const canonicalData =
//       data && typeof data === "object"
//         ? data
//         : payload && typeof payload === "object"
//           ? payload
//           : rest;

//     const envelope = {
//       app: module,
//       type,
//       data: canonicalData ?? {},
//     };

//     this.socket.send(JSON.stringify(envelope));
//   }

//   // =========================================================
//   // RECEIVE → Unified dispatcher
//   // =========================================================
//   private handleIncomingUnified(raw: any) {
//     // --------------------------------------------------
//     // Socket-level heartbeat
//     // --------------------------------------------------
//     if (raw?.type === "ping" && !raw?.app) {
//       this.sendRaw({ type: "pong" });
//       return;
//     }

//     // --------------------------------------------------
//     // System messages (no app)
//     // --------------------------------------------------
//     if (!raw?.app) {
//       this.emit(raw?.type, raw);
//       return;
//     }

//     // --------------------------------------------------
//     // Canonical app event envelope
//     // --------------------------------------------------
//     if (raw?.type === "event") {
//       const eventPayload =
//         raw?.data !== undefined
//           ? raw.data
//           : raw?.payload !== undefined
//             ? raw.payload
//             : {};

//       // 1) namespaced: "conversation:mark_as_read"
//       this.emit(`${raw.app}:${raw.event}`, eventPayload);

//       // 2) short: "mark_as_read"
//       this.emit(raw.event, eventPayload);

//       // 3) app-level: "conversation"
//       this.emit(raw.app, eventPayload);

//       return;
//     }

//     // --------------------------------------------------
//     // Temporary compatibility path
//     // --------------------------------------------------
//     // For any remaining non-event app messages, expose:
//     // - app key
//     // - type key
//     // - namespaced app:type key
//     const compatPayload =
//       raw?.data !== undefined
//         ? raw.data
//         : raw?.payload !== undefined
//           ? raw.payload
//           : raw;

//     this.emit(raw.app, compatPayload);
//     this.emit(raw.type, compatPayload);
//     this.emit(`${raw.app}:${raw.type}`, compatPayload);
//   }

//   // =========================================================
//   // Low-level sendRaw
//   // =========================================================
//   private sendRaw(data: any) {
//     if (this.socket && this.socket.readyState === WebSocket.OPEN) {
//       this.socket.send(JSON.stringify(data));
//     }
//   }

//   // =========================================================
//   // EVENT API
//   // =========================================================
//   public on(key: string, callback: WsHandler) {
//     if (!this.eventHandlers[key]) {
//       this.eventHandlers[key] = [];
//     }
//     this.eventHandlers[key].push(callback);
//   }

//   public off(key: string, callback: WsHandler) {
//     if (!this.eventHandlers[key]) return;

//     this.eventHandlers[key] = this.eventHandlers[key].filter(
//       (cb) => cb !== callback
//     );

//     if (this.eventHandlers[key].length === 0) {
//       delete this.eventHandlers[key];
//     }
//   }

//   private emit(key: string | undefined, payload: any) {
//     if (!key) return;

//     const handlers = this.eventHandlers[key];
//     if (!handlers?.length) return;

//     handlers.forEach((cb) => {
//       try {
//         cb(payload);
//       } catch (err) {
//         logger.error(`[WS] Handler error for key ${key}`, err);
//       }
//     });
//   }

//   // =========================================================
//   // STATUS
//   // =========================================================
//   public isConnected(): boolean {
//     return this.socket?.readyState === WebSocket.OPEN;
//   }

//   // =========================================================
//   // DISCONNECT
//   // =========================================================
//   public disconnectAll() {
//     this.intentional = true;
//     this.socket?.close();
//     this.socket = null;
//     this.eventHandlers = {};
//     this.registeredModules.clear();
//   }

//   // =========================================================
//   // RECONNECT
//   // =========================================================
//   private scheduleReconnect() {
//     if (!this.isUserLoggedIn()) return;

//     this.reconnectAttempts++;
//     if (this.reconnectAttempts > this.maxReconnectAttempts) {
//       logger.warn("[WS] Max reconnect attempts reached");
//       return;
//     }

//     const delay = Math.min(30000, 2000 * Math.pow(1.5, this.reconnectAttempts));
//     const jitter = Math.random() * 3000;
//     const total = delay + jitter;

//     logger.warn(`[WS] Reconnecting in ${Math.round(total)}ms...`);

//     setTimeout(() => {
//       void this.startConnection();
//     }, total);
//   }

//   private isUserLoggedIn() {
//     return !!localStorage.getItem("access_token");
//   }
// }

// const websocketManager = new SingleWebSocketManager();
// export default websocketManager;
